import React, { useEffect, useRef } from "react";
import "./App.css";
import heroImage from './hero.png';
import tokenImage from './token.png';

export default function App() {
  const gridRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    // Dynamic grid animation for protocol section
    const grid = gridRef.current;
    if (grid) {
      const cells = [];
      
      // Create 6x6 grid
      for (let i = 0; i < 36; i++) {
        const cell = document.createElement('div');
        cell.className = 'grid-cell';
        
        // Randomly assign some cells as active or nodes
        const rand = Math.random();
        if (rand < 0.15) {
          cell.classList.add('active');
        } else if (rand < 0.2) {
          cell.classList.add('node');
        }
        
        grid.appendChild(cell);
        cells.push(cell);
      }
      
      // Animate random cells
      const animateCells = () => {
        cells.forEach(cell => {
          if (cell.classList.contains('active') || cell.classList.contains('node')) {
            // Randomly toggle active state for some cells
            if (Math.random() < 0.03) {
              cell.classList.toggle('active');
            }
          } else {
            // Occasionally activate inactive cells
            if (Math.random() < 0.008) {
              cell.classList.add('active');
              setTimeout(() => {
                cell.classList.remove('active');
              }, 2000);
            }
          }
        });
      };
      
      const intervalId = setInterval(animateCells, 1000);
      
      return () => {
        clearInterval(intervalId);
      };
    }
  }, []);

  return (
    <div className="app">
      {/* HERO - Keep exactly as you had it */}
      <section className="hero" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>Pajama Protocol</h1>
          <p className="hero-subtitle">
            The coziest way to prove you showed up. A playful, presence-based check-in system for real human connections.
          </p>
          <div className="hero-actions">
            <a href="#protocol" className="btn primary">How It Works</a>
            <a href="#tokenomics" className="btn secondary">Meet $PJ</a>
          </div>
        </div>
      </section>

      {/* PROTOCOL - Redesigned with softer language */}
      <section id="protocol" className="section protocol">
        <div className="section-header">
          <h2 className="section-title">What's This Pajama Party All About?</h2>
          <p className="section-subtitle">
            Think of it as show-and-tell for grown-ups. Prove you're present, get rewarded, and build trust—all while staying cozy.
          </p>
        </div>
        
        
        <div className="cards-container">
          <div className="card">
            <div className="card-icon">🎪</div>
            <h3>Show Up & Be Counted</h3>
            <p>
              Pull on your PJs, join the video chat, and let us know you're here. It's about real people showing up for each other.
            </p>
          </div>
          <div className="card">
            <div className="card-icon">🤝</div>
            <h3>Play Fair, Stay Cozy</h3>
            <p>
              We're all here to have a good time. The system gently reminds everyone to play by the rules so we can all enjoy the party.
            </p>
          </div>
          <div className="card">
            <div className="card-icon">⏰</div>
            <h3>Three Strikes & You're Out</h3>
            <p>
              Miss too many check-ins? That's a strike. Three strikes means it's time for a nap—you'll sit out the next few rounds.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS - Redesigned with softer language */}
      <section className="section how-it-works alt">
        <div className="section-header">
          <h2 className="section-title">How Our Weekly PJ Party Works</h2>
          <p className="section-subtitle">
            Every Wednesday at specific times, pajama-clad participants gather to prove they're present and earn rewards.
          </p>
        </div>
        
        <div className="steps">
          <div className="connecting-line"></div>
          
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Dress for Success (PJs Required)</h3>
              <p>Grab your comfiest pajamas and join the video chat at the appointed time, three times every Wednesday.</p>
            </div>
          </div>
          
          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>Your Host Checks You In</h3>
              <p>The Party Host verifies you're present and submits your check-in to the network. No paperwork required!</p>
            </div>
          </div>
          
          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>The Network Agrees</h3>
              <p>Pajama Nodes check the reports and confirm that everything looks right. Most nodes have to agree to move forward.</p>
            </div>
          </div>
          
          <div className="step">
            <div className="step-number">4</div>
            <div className="step-content">
              <h3>Celebrate & Learn</h3>
              <p>Valid check-ins earn you rewards! Hosts and Nodes held accountable through STRIKES and OUTS.</p>
            </div>
          </div>
        </div>
      </section>

      {/* TOKENOMICS - Redesigned with token image */}
      <section id="tokenomics" className="section tokenomics">
        <div className="section-header">
          <h2 className="section-title">Meet $PJ - The Cozy Token</h2>
          <p className="section-subtitle">
             $PJ rewards participation, powers the network, and keeps everything fair and comfy-womfy.
          </p>
        </div>
        
        <div className="token-visual">
          <div className="token-image-container">
            <img src={tokenImage} alt="$PJ Token" className="token-image" />
            <div className="token-glow"></div>
          </div>
          <div className="token-stats">
            <div className="token-stat">
              <span className="stat-value">50%</span>
              <span className="stat-label">For showing up on time in pajamas</span>
            </div>
            <div className="token-stat">
              <span className="stat-value">15%</span>
              <span className="stat-label">For stuffed animals and mugs</span>
            </div>
            <div className="token-stat">
              <span className="stat-value">15%</span>
              <span className="stat-label">For a good reading</span>
            </div>
            <div className="token-stat">
              <span className="stat-value">20%</span>
              <span className="stat-label">At host discretion</span>
            </div>
          </div>
        </div>
        
        {/* Complete Tokenomics Information */}
        <div className="complete-tokenomics">
          <div className="tokenomics-overview">
            <h3 className="overview-title">Tokenomics Overview</h3>
            <div className="supply-info">
              <div className="supply-card total-supply">
                <span className="supply-amount">1,000,000,000</span>
                <span className="supply-label">Total $PJ Supply</span>
              </div>
              <p className="supply-note">
                $PJ is a distribution token used for participation, coordination, and protocol trust — not a claim on revenue.
              </p>
            </div>
            
            <div className="allocation-grid">
              <div className="allocation-card">
                <div className="allocation-header">
                  <span className="allocation-percentage">50%</span>
                  <span className="allocation-amount">500M $PJ</span>
                </div>
                <h4>Protocol Reserves</h4>
                <p>Ongoing Pajama Day distributions + future mechanics</p>
              </div>
              
              <div className="allocation-card">
                <div className="allocation-header">
                  <span className="allocation-percentage">25%</span>
                  <span className="allocation-amount">250M $PJ</span>
                </div>
                <h4>Initial Liquidity</h4>
                <p>Market formation</p>
              </div>
              
              <div className="allocation-card">
                <div className="allocation-header">
                  <span className="allocation-percentage">12.5%</span>
                  <span className="allocation-amount">125M $PJ</span>
                </div>
                <h4>2025 Pajama Day Check-In Airdrop</h4>
                <p>Vested linearly over 12 months</p>
              </div>
              
              <div className="allocation-card">
                <div className="allocation-header">
                  <span className="allocation-percentage">12.5%</span>
                  <span className="allocation-amount">125M $PJ</span>
                </div>
                <h4>2025 Pajama Day NYE Party</h4>
                <p>Vested linearly over 12 months</p>
              </div>
            </div>
          </div>
          
          {/* Hosting Rights & Decentralization */}
          <div className="hosting-section">
            <h3 className="hosting-title">Hosting Rights & Decentralization</h3>
            
            <div className="hosting-grid">
              <div className="hosting-card">
                <div className="hosting-icon">🏠</div>
                <h4>Hosting Requirement (Initial Phase)</h4>
                <p>To host official Pajama Day Check-Ins that qualify for $PJ distribution:</p>
                <div className="hosting-requirement">
                  <span className="requirement-amount">100,000,000 $PJ</span>
                  <span className="requirement-label">Minimum Holding</span>
                </div>
                <p className="hosting-note">
                  Hosting rights are stake-based, not permission-based.
                </p>
              </div>
              
              <div className="hosting-card">
                <div className="hosting-icon">👥</div>
                <h4>Joint Pajama Lockers</h4>
                <p>Users may <strong>Pajama Together</strong> by pooling $PJ into a joint locker:</p>
                <ul className="locker-features">
                  <li>A locker with ≥100,000,000 $PJ qualifies for hosting rights</li>
                  <li>Hosting is valid only if a majority of locker contributors are present at the check-in</li>
                </ul>
                <div className="locker-benefits">
                  <p><strong>This enables:</strong></p>
                  <ul>
                    <li>Smaller holders to coordinate</li>
                    <li>Keeping hosting social, not purely financial</li>
                    <li>Preventing absentee capital control</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="decay-section">
              <div className="decay-icon">📉</div>
              <div className="decay-content">
                <h4>Hosting Threshold Decay (Planned)</h4>
                <p>The required $PJ balance to host decreases over time to:</p>
                <ul>
                  <li>Reduce concentration</li>
                  <li>Encourage organic decentralization</li>
                </ul>
                <p className="decay-note">
                  Decay schedule is governed by protocol maturity, number of active hosts, and Pajama Node count.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="tokenomics-grid">
          <div className="token-item">
            <h4><span className="token-bullet">🎁</span> What Can You Do With $PJ?</h4>
            <p>Earn tokens for checking in until you have enough to Host your own pajama day check-ins!</p>
          </div>
          
          <div className="token-item">
            <h4><span className="token-bullet">🌟</span> How Do You Earn Them?</h4>
            <p>Show up on time in your PJs with a stuffed animal and a cup of mysterious liquid and read a poem!</p>
          </div>
          
          <div className="token-item">
            <h4><span className="token-bullet">🛡️</span> Keeping It Fair</h4>
            <p>It costs more to try to cheat than you could ever gain. We've made playing fair the most rewarding option for everyone.</p>
          </div>
        </div>
      </section>

      {/* SECURITY - Redesigned with softer language */}
      <section className="section security alt">
        <div className="section-header">
          <h2 className="section-title">Keeping Our Party Cozy & Safe</h2>
          <p className="section-subtitle">
            Think of it as a friendly bouncer for our digital pajama party—here to make sure everyone has a good time and plays nice.
          </p>
        </div>
        
        <div className="security-grid">
          <div className="security-item">
            <div className="security-icon">🏠</div>
            <h3>Responsible Hosts</h3>
            <p>Our hosts take their role seriously. If they make mistakes, they'll don't take a short break, they get banned.</p>
          </div>
          
          <div className="security-item">
            <div className="security-icon">👀</div>
            <h3>Helpful Observers</h3>
            <p>The nodes watching over everything make sure reports are accurate. They keep the hosts in check.</p>
          </div>
          
          <div className="security-item">
            <div className="security-icon">✨</div>
            <h3>One Real You</h3>
            <p>We make sure you're you—not ten copies of you trying to crash the party. Real people only.</p>
          </div>
        </div>
      </section>

      {/* CTA - Redesigned with softer language */}
      <section className="cta">
        <h2>Ready to Join Our Cozy Community?</h2>
        <p>Pull on your favorite PJs, grab a warm drink, and become part of something special. It's infrastructure that feels like a hug.</p>
        <a href="https://discord.gg/UMHM9JSPwK" target="_blank" className="btn primary">Grab Your Spot</a>
        <p className="cta-note">Next pajama party: This Wednesday!</p>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <p>© {new Date().getFullYear()} Pajama Protocol • Stay cozy, stay present.</p>
        <p className="footer-note">Remember: Real connections happen when real people show up.</p>
      </footer>
    </div>
  );
}